import type { SessionState } from './auth';
import { apiFetch } from './http';

/**
 * One live session of the identity (`GET /auth/sessions`, 08 §2.5.3): device, IP and the three
 * timestamps that matter to a person deciding whether to close it, with `isCurrent` marking the
 * one this very request is riding on.
 */
export interface SessionSummary {
  id: string;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  activeTenantId: string | null;
  isCurrent: boolean;
}

/** Codes a `422` of `PasswordPolicy` carries on the password field: `/auth/password/change` and `/auth/password/reset` (`errors.newPassword`), `/auth/invitations/accept` (`errors.password`). */
export const PASSWORD_ERROR_CODES = {
  tooShort: 'too_short',
  tooCommon: 'too_common',
} as const;

export const sessionsApi = {
  /**
   * `POST /auth/password/change` (08 §2.5.3): a correct current password changes it, closes
   * every other session of the identity and renews this one's cookie - the answer is the
   * refreshed session state, same shape `GET /auth/session` gives.
   */
  changePassword: (body: { currentPassword: string; newPassword: string }): Promise<SessionState> =>
    apiFetch<SessionState>('/api/v1/auth/password/change', { method: 'POST', body }),
  /** `GET /auth/sessions`: every live session of the identity, current one marked. */
  list: (signal?: AbortSignal): Promise<SessionSummary[]> =>
    apiFetch<SessionSummary[]>('/api/v1/auth/sessions', signal === undefined ? {} : { signal }),
  /** `DELETE /auth/sessions/{id}` → `204`; revoking the current one also clears its cookie. */
  revoke: (id: string): Promise<void> =>
    apiFetch<void>(`/api/v1/auth/sessions/${id}`, { method: 'DELETE' }),
};

/** Query key of `GET /auth/sessions`. */
export const SESSIONS_KEY = ['auth', 'sessions'] as const;
